import styled from 'styled-components'
import { animated, config, SpringValue, TransitionFn, useTransition } from '@react-spring/web'
import { OverlayContainer } from '@react-aria/overlays'

import { ModalDialog } from 'components/core/Dialog'
import { useEffect, useState } from 'react'
import { PrimaryButton } from 'components/core/Buttons'
import { useClipboard } from 'use-clipboard-copy'
import { trpc } from '~/utils/trpc'
import { nanoid } from 'utils/nano'
import { useGobblerAuth } from 'hooks/useGobblerAuth'

export enum InviteArtistModalState {
  closed,
  open,
}

export type Styles = {
  opacity: SpringValue<number>
  y: SpringValue<number>
  scale: SpringValue<number>
}

const AnimatedOverlayContainer = animated(OverlayContainer)

export function useModalTransition(): [
  TransitionFn<InviteArtistModalState, { opacity: number; y: number; scale: number }>,
  InviteArtistModalState,
  (state: InviteArtistModalState) => void,
] {
  const [modalState, setModalState] = useState<InviteArtistModalState>(
    InviteArtistModalState.closed,
  )

  const dialogTransitions = useTransition(modalState, {
    from: {
      opacity: 0,
      y: -10,
      // Scale is for goo bg
      scale: 0.3,
    },
    enter: {
      opacity: 1,
      y: 0,
      scale: 1,
      config: (key: string) => {
        const clamp = key === 'scale' ? false : true
        return { ...config.wobbly, clamp }
      },
    },
    leave: {
      opacity: 0,
      y: 10,
      scale: 0.8,
      config: {
        clamp: true,
      },
    },
    config: config.wobbly,
  })

  return [dialogTransitions, modalState, setModalState]
}

function InviteArtistModal({
  modalState,
  setModalState,
  styles,
}: {
  modalState: InviteArtistModalState
  setModalState: (state: InviteArtistModalState) => void
  styles: Styles
}) {
  const clipboard = useClipboard({ copiedTimeout: 1500 })

  const auth = useGobblerAuth()

  const createSubmissionInviteMutation = trpc.useMutation(
    ['submissions.createSubmissionInvite'],
    {},
  )

  // Create submission on mount
  useEffect(() => {
    createSubmissionInviteMutation.mutate({})
  }, [auth.id])

  // TODO: Code real values for sharing.
  const code = createSubmissionInviteMutation.data?.invite_code

  const shareUrlView = `artgobblers.com/invite?invite=`

  const shareUrl = `https://artgobblers.com/invite?invite=${code}`

  const { copied } = clipboard

  return (
    <AnimatedOverlayContainer
      style={{ position: 'relative', opacity: styles.opacity, zIndex: 10000 }}
      key={modalState}
    >
      <ModalDialog
        shouldCloseOnBlur
        shouldCloseOnInteractOutside={(_el) => true}
        isDismissable={false}
        allowScroll={false}
        isKeyboardDismissDisabled={false}
        containerStyles={{
          position: 'relative',

          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',

          width: '100%',
          maxWidth: 424,
          paddingLeft: 24,
          paddingRight: 24,

          height: 424,

          margin: '0 10px',
          transform: styles.y.interpolate(
            (value) => `translate3d(0px, ${value ?? 0}px, 0px)`,
          ) as any,
          boxShadow: `0px 24px 60px rgba(0, 0, 0, 0.06), 0px 12px 20px rgba(0, 0, 0, 0.05), 0px 4px 8px rgba(0, 0, 0, 0.02)`,
          background: '#FFFFFF',
          border: '3px solid #080A0C',
          borderRadius: '40px',
        }}
        gooContainerStyles={{ scale: styles.scale as any }}
        isOpen
        showGooBackground
        onClose={() => {
          setModalState(InviteArtistModalState.closed)
        }}
      >
        <Title>Invite an artist to draw for you</Title>
        <Subtitle>Share this unique drawing link via private message or Twitter.</Subtitle>
        {/* <InviteCodeDiv>Invite Code: <span style={{ display: 'inline', marginLeft: 4 }}><strong>{code}</strong></span></InviteCodeDiv> */}
        <InviteCodeDiv>
          {shareUrlView}
          <span style={{ display: 'inline' }}>
            <strong>{code}</strong>
          </span>
        </InviteCodeDiv>

        <div>{shareUrlView}</div>
        <PrimaryButton
          onPress={() => {
            clipboard.copy(shareUrl)
          }}
          buttonTextStyles={{ color: copied ? '#8EF42E' : 'black' }}
          buttonType={copied ? 'primary_white' : 'success'}
        >
          {copied ? 'Copied!' : 'Copy link'}
        </PrimaryButton>
      </ModalDialog>
    </AnimatedOverlayContainer>
  )
}

const Title = styled.div`
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  color: #080a0c;
  max-width: 300px;
`

const Subtitle = styled(Title)`
  font-size: 18px;
  font-weight: 400;
  margin-top: 12px;
  margin-bottom: 32px;
`

const InviteCodeDiv = styled.div`
  font-weight: 600;
  font-size: 18px;

  padding: 20px 14px;
  margin-bottom: 12px;

  color: #080a0c;
  display: flex;
  align-items: center;

  min-height: 70px;
  background: #eaebf1;
  border-radius: 4px;

  width: 100%;
  text-align: left;

  border: 3px solid #b8bccf;
  border-radius: 16px;
`

export { InviteArtistModal }
